import { useCallback, useState } from 'react';

/**
 * Which groups (weeks) of a list are folded away. Everything starts open. The
 * choice lives as long as the component that calls this, so it survives
 * filtering and paging but not a reload. `keys` are the groups on screen now:
 * "collapse all" folds those.
 */
export function useCollapsibleGroups(keys: string[]) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  const isExpanded = useCallback((key: string) => !collapsed.has(key), [collapsed]);

  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const collapseAll = useCallback(() => setCollapsed(new Set(keys)), [keys]);

  const allExpanded = keys.every((key) => !collapsed.has(key));

  return { isExpanded, toggle, expandAll, collapseAll, allExpanded };
}
